// src/components/MissionDetailsModal.js
import React, { useEffect, useState } from 'react';
import { getMissionDetails } from '../api/api';
import styles from '../pages/AdminLists.module.css'; // On réutilise le même CSS

const MissionDetailsModal = ({ missionId, onClose }) => {
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('infos');

    useEffect(() => {
        if (missionId) {
            fetchDetails();
        }
    }, [missionId]);

    const fetchDetails = async () => {
        setLoading(true);
        try {
            const res = await getMissionDetails(missionId);
            setDetails(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // --- FORMATTEURS ---
    const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';
    const formatCurrency = (amount) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);

    // AJOUT DE LA FONCTION FORMATSIRET
    const formatSiret = (siret) => {
        if (!siret) return '-';
        if (typeof siret === 'string' && siret.includes('E+')) {
            const num = parseFloat(siret.replace(',', '.'));
            return num.toLocaleString('fullwide', { useGrouping: false });
        }
        return String(siret).replace('.0', '');
    };

    const getInvoiceStateBadge = (state) => {
        const s = (state || '').toLowerCase();
        if (s.includes('paid') || s.includes('payé')) return styles.badgeSuccess;
        if (s.includes('sent') || s.includes('envoyé')) return styles.badgeInfo;
        if (s.includes('late') || s.includes('retard')) return styles.badgeDanger;
        return styles.badgeWarning;
    };

    if (!missionId) return null;

    return (
        <div className={styles.modalOverlay} style={{ zIndex: 1100 }}> {/* zIndex + élevé pour passer au dessus */}
            <div className={styles.modalContent} style={{ maxWidth: '900px' }}>
                <button className={styles.closeBtn} onClick={onClose}>&times;</button>
                
                {loading || !details ? <div className={styles.loading}>Chargement de la mission...</div> : (
                    <>
                        <h2 className={styles.modalTitle}>
                            Mission #{details.info.order_id} 
                            <span style={{fontSize:'0.6em', color:'#666', marginLeft:'10px'}}>
                                ({details.info.clientName})
                            </span>
                        </h2>

                        {/* ONGLETS */}
                        <div className={styles.tabs}>
                            <button 
                                className={`${styles.tab} ${activeTab === 'infos' ? styles.activeTab : ''}`} 
                                onClick={() => setActiveTab('infos')}
                            >Informations</button>
                            <button 
                                className={`${styles.tab} ${activeTab === 'paiements' ? styles.activeTab : ''}`} 
                                onClick={() => setActiveTab('paiements')}
                            >Paiements & Factures ({details.invoices.length})</button>
                        </div>

                        <div className={styles.tabContent}>
                            
                            {/* ONGLET 1: INFOS GÉNÉRALES */}
                            {activeTab === 'infos' && (
                                <div className={styles.infoGrid}>
                                    <div className={styles.infoItem}>
                                        <label>Consultant</label>
                                        <span>{details.info.firstName} {details.info.lastName}</span>
                                        <small>{details.info.resourceEmail}</small>
                                    </div>
                                    <div className={styles.infoItem}>
                                        <label>Partenaire</label>
                                        <span>{details.info.clientName}</span>
                                        <small>SIRET: {formatSiret(details.info.siret)}</small>
                                    </div>
                                    <div className={styles.infoItem}>
                                        <label>Période</label>
                                        <span>Du {formatDate(details.info.startDate)} au {formatDate(details.info.endDate)}</span>
                                    </div>
                                    <div className={styles.infoItem}>
                                        <label>TJM Vendu</label>
                                        <span style={{fontSize:'1.2rem', fontWeight:'bold', color:'#2c3e50'}}>
                                            {details.info.TJM} €
                                        </span>
                                    </div>
                                    <div className={styles.infoItem}>
                                        <label>CA Simulé HT</label>
                                        <span>{formatCurrency(details.info.turnoverSimulatedExcludingTax)}</span>
                                    </div>
                                </div>
                            )}

                            {/* ONGLET 2: PAIEMENTS (FACTURES) */}
                            {activeTab === 'paiements' && (
                                <div style={{overflowX: 'auto'}}>
                                    <table className={styles.tableSmall}>
                                        <thead>
                                            <tr>
                                                <th>Mois Facture</th>
                                                <th>Détail Hors HNO (HT / Jours)</th>
                                                <th>Détail HNO (HT / Jours)</th>
                                                <th>Montant HT</th>
                                                <th>Montant TTC</th>
                                                <th>Date Prévue</th>
                                                <th>État</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {details.invoices.map((inv, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        {inv.factureDate ? new Date(inv.factureDate).toLocaleString('fr-FR', { month: 'long', year: 'numeric' }) : '-'}
                                                    </td>
                                                    <td>
                                                        <div style={{fontWeight:'500'}}>{formatCurrency(inv.ht_normal)}</div>
                                                        <small style={{color:'#666'}}>{inv.quantity_Normal || 0} jours</small>
                                                    </td>
                                                    <td>
                                                        <div style={{color: inv.ht_hno > 0 ? '#27ae60' : '#666'}}>
                                                            {formatCurrency(inv.ht_hno)}
                                                        </div>
                                                        <small style={{color:'#666'}}>{inv.quantity_HNO || 0} jours</small>
                                                    </td>
                                                    <td>{formatCurrency(inv.montant_ht)}</td>
                                                    <td>{formatCurrency(inv.montant_ttc)}</td>
                                                    <td>{formatDate(inv.expectedPaymentDate)}</td>
                                                    <td>
                                                        <span className={`${styles.badge} ${getInvoiceStateBadge(inv.stateFacture)}`}>
                                                            {inv.stateFacture || 'Inconnu'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                            {details.invoices.length === 0 && (
                                                <tr><td colSpan="5" style={{textAlign:'center'}}>Aucune facture.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default MissionDetailsModal;